import React from 'react';
import { useTheme } from 'styled-components';

import useBreakpoint from 'Common/hooks/useBreakpoint';

import Main from 'Elements/Main';
import Section from 'Elements/Section';
import { H4 } from 'Elements/Typography';
import Wave from 'Elements/Wave';

import Breadcrumbs, { BreadcrumbsProps } from 'Components/Breadcrumbs';
import Grid, { Col } from 'Components/Grid';
import Card, { CardProps } from 'Components/Card';
import Accordion, { AccordionProps } from 'Components/Accordion';

import HeroBanner, { StaffHeroBannerProps } from 'Modules/HeroBanner/Staff';

import { ReactComponent as profileWave } from 'Public/graphics/waves/staff-profile/staff-profile-wave.svg';

export interface Props {
    breadcrumbs: BreadcrumbsProps;
    heroBanner: StaffHeroBannerProps;
    biography: {
        heading: string;
        content: React.ReactNode;
    };
    courses: {
        heading: string;
        cards: (CardProps & { id: string })[];
    };
    furtherInformation: {
        heading: string;
        items: AccordionProps[];
    };
}

const Academic: React.FC<Props> = props => {
    const { heroBanner, breadcrumbs, biography, courses, furtherInformation } = props;

    const { breakpoints, fonts } = useTheme();
    const isLargeBreakpoint = useBreakpoint(breakpoints.large.value);

    return (
        <Main>
            <Breadcrumbs {...breadcrumbs} />
            <HeroBanner {...heroBanner} />
            <Section id="biography" background overflow="hidden">
                {isLargeBreakpoint && (
                    <Wave
                        svg={{
                            wave: profileWave,
                            position: {
                                medium: {
                                    top: '-5rem',
                                    right: '-27.5rem'
                                }
                            }
                        }}
                        width={2088}
                        height={2088}
                    />
                )}
                <Grid>
                    <Col m={9} h={7}>
                        <h2>{biography.heading}</h2>
                        {biography.content}
                    </Col>
                </Grid>
            </Section>
            {furtherInformation.items.length > 0 && (
                <Section id="further-information" paddingTop={1}>
                    <Grid>
                        <Col h={10}>
                            <h2>{furtherInformation.heading}</h2>
                            {furtherInformation.items.map((item, index) => (
                                <Accordion
                                    {...item}
                                    key={item.id}
                                    defaultOpen={index === 0}
                                    heading={
                                        <H4 as="span" fontFamily={fonts.secondary} weight="300">
                                            {item.heading}
                                        </H4>
                                    }
                                />
                            ))}
                        </Col>
                    </Grid>
                </Section>
            )}
            {courses.cards.length > 0 && (
                <Section id="taught-courses" background paddingTop={1}>
                    <Grid>
                        <Col>
                            <h2>{courses.heading}</h2>
                            <Grid rowGap="1rem">
                                {courses.cards.map(({ id, ...rest }) => (
                                    <Col key={id} m={6} xl={4}>
                                        <Card {...rest} />
                                    </Col>
                                ))}
                            </Grid>
                        </Col>
                    </Grid>
                </Section>
            )}
        </Main>
    );
};

export default Academic;
